import { AuthService } from './../../core/authentication/auth.service';
import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { EMPTY, firstValueFrom, merge } from 'rxjs';
import { finalize, first, switchMap, tap } from 'rxjs/operators';
import { Messages } from 'src/app/common/enums/messages';
import { AbsenceService } from 'src/app/data/absence.service';
import { AbsenceApprovalPermissionDto, AbsenceDto } from 'src/app/models/dtos/absence-dto';
import { AbsenceStatus } from 'src/app/models/enums/absence-status';
import { WorkerProfile } from 'src/app/models/WorkerProfile';
import { ConfirmDialogComponent } from 'src/app/shared/messages/confirm-dialog/confirm-dialog.component';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { AbsenceModalComponent } from '../absence-form/absence-form.component';
import { WorkerAbsencesListDataSource } from './worker-absences-list.datasource';
import { AbsenceType } from 'src/app/models/enums/absence-type';
import { SickLeaveModalComponent } from '../sick-leaves-modal/sick-leaves-modal.component';
import { DatePipe } from '@angular/common';
import { Permission } from 'src/app/common/enums';

@Component({
  selector: 'app-worker-absences-list',
  templateUrl: './worker-absences-list.component.html',
  styleUrls: ['./worker-absences-list.component.scss'],
  providers: [DatePipe],
  standalone: false
})
export class WorkerAbsencesListComponent implements OnInit, OnChanges, AfterViewInit {
  dataSource = inject(WorkerAbsencesListDataSource);
  private absenceService = inject(AbsenceService);
  private dialog = inject(MatDialog);
  private route = inject(ActivatedRoute);
  private translateService = inject(TranslateService);
  private snackbar = inject(SnackBarService);
  private spinner = inject(NgxSpinnerService);
  private authService = inject(AuthService);
  private datePipe = inject(DatePipe);

  profile: WorkerProfile;
  displayedColumns = ['employer', 'employerObject', 'absenceType', 'sentToApprovalOn', 'startDate', 'endDate', 'businessDays', 'absenceReason', 'status', 'actions'];
  displayedColumnsMobile = ['absenceType', 'date', 'businessDays', 'status', 'actions'];

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  @Input() refreshToken: number;
  @Input() workerAgreementId: number | null;
  @Output() refreshTokenChange = new EventEmitter<number>();

  constructor() { }

  ngOnChanges(changes: SimpleChanges): void {
    if (!!changes.workerAgreementId?.currentValue || !!changes.refreshToken?.currentValue) this.fetchAbsences();
  }

  ngOnInit(): void {
    this.route.parent.data.subscribe((data) => (this.profile = data['workerProfile']));
  }

  ngAfterViewInit(): void {
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
    this.translateService.onLangChange.subscribe(() => (this.paginator.pageIndex = 0));

    merge(this.sort.sortChange, this.paginator.page, this.translateService.onLangChange)
      .pipe(tap(() => this.fetchAbsences()))
      .subscribe();

    this.dataSource.absencesSubject.next({
      WorkerId: this.profile.WorkerId,
      WorkerAgreementId: this.workerAgreementId,
      Page: 1,
      PageSize: 10,
      SortingField: 'StartDate',
      SortingDirection: 'desc',
    });
  }

  changeRefreshToken() {
    this.refreshTokenChange.emit(Math.random());
  }

  openModal(record?: AbsenceDto) {
    this.dialog
      .open(AbsenceModalComponent, {
        maxHeight: '90vh',
        data: {
          WorkerId: this.profile.WorkerId,
          Absence: record,
          WorkerAgreementId: record.WorkerAgreementId,
          EmploymentTypeId: record.EmploymentTypeId
        },
      })
      .afterClosed()
      .pipe(first())
      .subscribe((isCreated: boolean) => {
        if (isCreated) {
          this.fetchAbsences();
        }
      });
  }

  isSendToApprovalButtonVisible = (absenceStatusId: number, isApprovalRequired: boolean): boolean =>
    isApprovalRequired && absenceStatusId === AbsenceStatus.Draft;

  sendToApproval(absenceId: number): void {
    const onConfirm = (absenceId) => {
      this.spinner.show();
      return this.absenceService.sendToApproval(absenceId).pipe(
        tap((_) => this.fetchAbsences()),
        tap((_) => this.snackbar.openSuccessSnackBar(Messages.SuccessfullySentToApprovalAbsence)),
        finalize(() => this.spinner.hide()),
      );
    };

    this.dialog
      .open(ConfirmDialogComponent, { data: { message: Messages.ConfirmSendingToApprovalAbsenceMessage } })
      .afterClosed()
      .pipe(
        first(),
        switchMap((isConfirmed) => (isConfirmed ? onConfirm(absenceId) : EMPTY)),
        tap(() => this.changeRefreshToken())
      )
      .subscribe();
  }

  isConfirmAbsenceButtonVisible = (absenceStatusId: number, isApprovalRequired: boolean): boolean =>
    !isApprovalRequired && absenceStatusId === AbsenceStatus.Draft;

  confirmAbsence(absenceId: number) {
    const onConfirm = (absenceId) => {
      this.spinner.show();
      return this.absenceService.confirmAbsence(absenceId).pipe(
        tap((_) => this.fetchAbsences()),
        tap((_) => this.snackbar.openSuccessSnackBar(Messages.SuccessfullyConfirmedAbsence)),
        finalize(() => this.spinner.hide()),
      );
    };

    this.dialog
      .open(ConfirmDialogComponent, { data: { message: Messages.ConfirmConfirmingAbsenceMessage } })
      .afterClosed()
      .pipe(
        first(),
        switchMap((isConfirmed) => (isConfirmed ? onConfirm(absenceId) : EMPTY)),
        tap(() => this.changeRefreshToken())
      )
      .subscribe();
  }

  isDeleteButtonVisible = (absenceStatusId: number) => absenceStatusId === AbsenceStatus.Draft;

  deleteAbsence(absenceId: number) {
    const onConfirm = (absenceId) => {
      this.spinner.show();
      return this.absenceService.deleteAbsence(absenceId).pipe(
        tap((_) => this.fetchAbsences()),
        tap((_) => this.snackbar.openSuccessSnackBar(Messages.SuccessfullyDeletedAbsence)),
        finalize(() => this.spinner.hide()),
      );
    };

    this.dialog
      .open(ConfirmDialogComponent, { data: { message: Messages.ConfirmDeletingAbsenceMessage } })
      .afterClosed()
      .pipe(
        first(),
        switchMap((isConfirmed) => (isConfirmed ? onConfirm(absenceId) : EMPTY)),
        tap(() => this.changeRefreshToken())
      )
      .subscribe();
  }

  isBackToDraftButtonVisible = (absenceStatusId: number) => absenceStatusId === AbsenceStatus.PendingApproval;

  backToDraftAbsence(absenceId: number) {
    const onConfirm = (absenceId) => {
      this.spinner.show();
      return this.absenceService.backToDraftAbsence(absenceId).pipe(
        tap((_) => this.fetchAbsences()),
        tap((_) => this.snackbar.openSuccessSnackBar(Messages.SuccessfullyBackToDraftAbsence)),
        finalize(() => this.spinner.hide()),
      );
    };

    this.dialog
      .open(ConfirmDialogComponent, { data: { message: Messages.ConfirmBackToDraftAbsenceMessage } })
      .afterClosed()
      .pipe(
        first(),
        switchMap((isConfirmed) => (isConfirmed ? onConfirm(absenceId) : EMPTY)),
        tap(() => this.changeRefreshToken())
      )
      .subscribe();
  }

  isAcceptAbsenceButtonVisible = (absenceStatusId: number) => (absenceStatusId === AbsenceStatus.PendingApproval && this.authService.authServerUserId !== this.profile.AuthServerUserId) ||
    ((absenceStatusId === AbsenceStatus.PendingApproval) && this.authService.hasPermission(Permission.AcceptAbsenceTierTwo));

  acceptAbsence(absenceId: number) {
    const onConfirm = () => {
      this.spinner.show();
      return this.absenceService.acceptAbsence(absenceId).pipe(
        first(),
        tap((_) => this.fetchAbsences()),
        tap((_) => this.snackbar.openSuccessSnackBar(Messages.SuccesfullyAcceptedAbsence)),
        finalize(() => this.spinner.hide()),
      );
    };

    this.dialog
      .open(ConfirmDialogComponent, { data: { message: Messages.ConfirmAcceptingAbsenceMessage } })
      .afterClosed()
      .pipe(
        first(),
        switchMap((isConfirmed) => (isConfirmed ? onConfirm() : EMPTY)),
        tap(() => this.changeRefreshToken())
      )
      .subscribe();
  }

  isRejectAbsenceButtonVisible = (absenceStatusId: number) => (absenceStatusId === AbsenceStatus.PendingApproval && this.authService.authServerUserId !== this.profile.AuthServerUserId) ||
    ((absenceStatusId === AbsenceStatus.PendingApproval) && this.authService.hasPermission(Permission.AcceptAbsenceTierTwo));

  rejectAbsence(absenceId: number) {
    const onConfirm = () => {
      this.spinner.show();
      return this.absenceService.rejectAbsence(absenceId).pipe(
        first(),
        tap((_) => this.fetchAbsences()),
        tap((_) => this.snackbar.openSuccessSnackBar(Messages.SuccesfullyRejectedAbsence)),
        finalize(() => this.spinner.hide()),
      );
    };

    this.dialog
      .open(ConfirmDialogComponent, { data: { message: Messages.ConfirmRejectingAbsenceMessage } })
      .afterClosed()
      .pipe(
        first(),
        switchMap((isConfirmed) => (isConfirmed ? onConfirm() : EMPTY)),
        tap(() => this.changeRefreshToken())
      )
      .subscribe();
  }

  isCancelAbsenceButtonVisible = (absenceStatusId: number, absenceTypeId: number): boolean => absenceStatusId === AbsenceStatus.Accepted && absenceTypeId !== AbsenceType.SickLeave;

  async cancelAbsence(absenceId: number) {
    const onConfirm = () => {
      this.spinner.show();
      return this.absenceService.cancelAbsence(absenceId).pipe(
        first(),
        tap((_) => this.fetchAbsences()),
        tap((_) => this.snackbar.openSuccessSnackBar(Messages.SuccessfullyCanceledAbsence)),
        finalize(() => this.spinner.hide()),
      );
    };

    await firstValueFrom(this.dialog
      .open(ConfirmDialogComponent, { data: { message: Messages.ConfirmCancellingAbsenceMessage } })
      .afterClosed()
      .pipe(
        first(),
        switchMap((isConfirmed) => (isConfirmed ? onConfirm() : EMPTY)),
        tap(() => this.changeRefreshToken())
      ));
  }

  openSickLeaveDetailsModal(absenceId: number) {
    this.dialog
      .open(SickLeaveModalComponent, {
        maxHeight: '90vh',
        maxWidth: '120vh',
        data: { SickLeaveId: absenceId },
      })
      .afterClosed()
      .pipe(first());
  }

  shouldShowStatusTooltip(element: AbsenceDto) {
    return element.StatusId === AbsenceStatus.Accepted ||
      element.StatusId === AbsenceStatus.Rejected ||
      element.StatusId === AbsenceStatus.PendingApproval
  }

  getStatusTooltip(element: AbsenceDto): string {
    if (element.IsImported) {
      return this.translateService.instant('AbsenceStatusTooltip.Imported');
    }

    if (!element.ApprovalRequired) {
      return this.translateService.instant('AbsenceStatusTooltip.NoApprovalRequired');
    }

    if (element.StatusId === AbsenceStatus.Accepted) {
      return this.translateService.instant('AbsenceStatusTooltip.HistoryInformation', {
        status: element.Status?.toLowerCase(),
        changedBy: element.AcceptedBy,
        changedDate: this.datePipe.transform(element.AcceptedOn, 'yyyy-MM-dd HH:mm')
      });
    }

    if (element.StatusId === AbsenceStatus.Rejected) {
      return this.translateService.instant('AbsenceStatusTooltip.HistoryInformation', {
        status: element.Status?.toLowerCase(),
        changedBy: element.RejectedBy,
        changedDate: this.datePipe.transform(element.RejectedOn, 'yyyy-MM-dd HH:mm')
      });
    }

    if (element.StatusId === AbsenceStatus.PendingApproval) {
      if (element.LastDecisiveApprovalDate && element.LastDecisiveApprovalUserName) {
        const decisionKey = element.LastDecisiveApprovalIsPositive
          ? 'AbsenceTooltip.AcceptedBy'
          : 'AbsenceTooltip.RejectedBy';

        return this.translateService.instant(decisionKey, {
          userName: element.LastDecisiveApprovalUserName,
          date: this.datePipe.transform(element.LastDecisiveApprovalDate, 'yyyy-MM-dd HH:mm')
        });
      }
    }

    return '';
  }

  private fetchAbsences(): void {
    if (!this.profile?.WorkerId) {
      return;
    }

    this.dataSource.absencesSubject.next({
      WorkerId: this.profile.WorkerId,
      WorkerAgreementId: this.workerAgreementId,
      Page: this.paginator.pageIndex + 1,
      PageSize: this.paginator.pageSize,
      SortingField: this.sort.active,
      SortingDirection: this.sort.direction,
    });
  }

  approvingButtonsVisible(element: AbsenceDto) {
    return element.ApprovalPermissions?.some((permissionDto) => this.authService.hasPermission(permissionDto.Permission as Permission)) || false;
  }
}
